import InactiveStepIcon from "@/assets/icons/inactive-step.svg?react";
import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

type TimelineItemProps = {
  dateLabel: string;
  timeLabel: string;
  title: ReactNode;
  subtitle?: ReactNode;
  location?: ReactNode;
  isLast: boolean;
  isSelected?: boolean;
  className?: string;
  onClick?: () => void;
};

export const TimelineItem = ({
  dateLabel,
  timeLabel,
  title,
  subtitle,
  location,
  isLast,
  isSelected = false,
  className,
  onClick,
}: TimelineItemProps) => {
  const inner = (
    <>
      {/* Desktop */}
      <div
        className={cn("hidden w-full flex-row items-start justify-between px-2 lg:flex", className)}
      >
        <div className="flex w-fit flex-row items-stretch gap-6">
          <div className="flex h-fit w-[90px] flex-col justify-center py-2">
            <span
              className={cn("text-xs font-medium", isSelected ? "text-blue-700" : "text-gray-700")}
            >
              {dateLabel}
            </span>
            <span className="text-xs font-semibold text-gray-600">{timeLabel}</span>
          </div>
          <div className="flex w-[24px] flex-col items-center">
            <InactiveStepIcon width={24} height={24} />
            <div
              className={cn("-mb-4 h-full flex-1 border-r-2 border-gray-200", isLast && "hidden")}
            />
          </div>
          <div className="flex flex-1 flex-col justify-center gap-1 text-sm">
            <span className={cn(isSelected && "text-blue-700")}>{title}</span>
            {subtitle && <span className="text-xs font-medium text-gray-700">{subtitle}</span>}
          </div>
        </div>
        {location && (
          <div className="overflow-hidden text-ellipsis whitespace-nowrap">
            <span className="truncate text-sm font-normal text-gray-500">{location}</span>
          </div>
        )}
      </div>
      {/* Mobile */}
      <div
        className={cn(
          "flex w-full flex-row items-stretch justify-between gap-4 lg:hidden",
          className,
        )}
      >
        <div className="flex flex-1 flex-row items-stretch gap-4 pb-4">
          <div className="flex w-[24px] flex-col items-center">
            <div className="h-[24px] w-[24px]">
              <InactiveStepIcon width={24} height={24} />
            </div>
            <div className={cn("-mb-8 flex-1 border-r-2 border-gray-200", isLast && "hidden")} />
          </div>
        </div>
        <div className="flex w-full flex-col items-start gap-0.5 overflow-hidden">
          <span className={cn(isSelected && "text-blue-700")}>{title}</span>
          {subtitle && <span className="text-xs font-medium text-gray-700">{subtitle}</span>}
          <div className="flex h-fit flex-1 flex-col gap-0">
            <span
              className={cn("text-xs font-medium", isSelected ? "text-blue-700" : "text-gray-700")}
            >
              {dateLabel}
            </span>
            <span className="text-xs font-medium text-gray-600">{timeLabel}</span>
          </div>
          {location && (
            <span className="truncate text-sm font-normal text-gray-500">{location}</span>
          )}
        </div>
      </div>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          "w-full text-left transition-colors hover:bg-gray-50",
          isSelected && "bg-blue-50 hover:bg-blue-50",
        )}
      >
        {inner}
      </button>
    );
  }

  return <>{inner}</>;
};
